// gameState.js - All game variables and initial state
import { createRng, randomSeed } from './rng';

export const createInitialGameState = () => {
  return {
    seed: 0,
    rng: createRng(0),
    inputLog: [],        // [{ t: tick, a: actionCode }] captured for run validation
    lastRunReceipt: null,
    // Deterministic simulation control (used by simulation.js + the validator)
    devMode: false,
    gameOver: false,
    gameOverReason: null, // 'enemy' | 'fall'
    deathEndTick: 0,
    shootCooldown: 0,
    shootPulse: false,    // one-shot shoot request (touch tap)
    events: [],           // per-tick side-effects for the live layer (sfx/particles)
    player: { x: 200, y: 300, w: 50, h: 62, vx: 0, vy: 0, floatOffset: 0, squash: 1.0, stretch: 1.0 },
    platforms: [],
    coins: [],
    boosts: [],
    enemies: [],
    projectiles: [],
    particles: [],
    shakeMag: 0,
    shakeFrames: 0,
    keys: {},
    score: 0,
    collectedCoins: 0,
    coinsForRocket: 0,
    coinsCollectedCount: 0, // raw count of coins picked up this run (no 2x)
    coinsSpawnedTotal: 0,   // total coins that appeared this run (the "/ Y")
    enemiesKilled: 0,
    shotsFired: 0,          // for accuracy: total projectiles fired
    enemyHits: 0,           // for accuracy: projectiles that struck an enemy
    cameraY: 0,
    backgroundOffset: 0,
    stars: [],
    gravity: 0.3,
    jumpPower: -9,
    springJumpPower: -18,
    moveSpeed: 4.5,
    platformSpeed: 2,
    activeBoost: null,
    boostEndTick: 0,
    rocketActive: false,
    rocketEndY: 0,
    rocketEndTick: 0,
    shieldActive: false,
    shieldEndTick: 0,
    shootingEnabled: true,
    bulletsRemaining: 10,
    maxBullets: 30,
    speedModifier: 1.0,
    speedModEndTick: 0,
    touchStartX: 0,
    touchStartY: 0,
    touchCurrentX: 0,
    touchTargetX: 0,   // finger position in canvas coords; player steers toward it
    touchDragDX: 0,    // relative slide accumulated since last tick (canvas px)
    touchMoved: false, // distinguishes a drag (move) from a tap (shoot)
    isTouching: false,
    // Tilt steering (optional, phone-only). Driven by deviceorientation; not reset
    // per-run so calibration persists. The validator never sees tilt (fresh state).
    tiltActive: false,     // true once orientation data is flowing
    tiltGamma: 0,          // smoothed left-right tilt angle (deg)
    tiltBaseline: 0,       // neutral angle captured when tilt is enabled
    tiltHasBaseline: false,
    hitEnemy: false,
    deathTime: 0,
    animationFrame: 0,
    magnetActive: false,
    magnetEndTick: 0,
    options: {
      enemiesEnabled: true,
      boostsEnabled: true,
      soundEnabled: true,
      tiltEnabled: false
    }
  };
};

export const resetGameState = (game, canvasWidth, canvasHeight, seed = randomSeed()) => {
  // Seed the deterministic gameplay RNG and start a fresh input recording so the
  // whole run can be reproduced/validated later from { seed, inputLog }.
  game.seed = seed;
  game.rng = createRng(seed);
  game.inputLog = [];
  game.lastRunReceipt = null;

  // Reset player
  game.player = { x: 200, y: 450, w: 50, h: 62, vx: 0, vy: -10, floatOffset: 0, squash: 1.0, stretch: 1.0 };

  // Reset arrays
  game.platforms = [];
  game.coins = [];
  game.boosts = [];
  game.enemies = [];
  game.projectiles = [];
  game.particles = [];
  game.shakeMag = 0;
  game.shakeFrames = 0;
  game.keys = {};
  
  // Reset counters
  game.score = 0;
  game.collectedCoins = 0;
  game.coinsForRocket = 0;
  game.coinsCollectedCount = 0;
  game.coinsSpawnedTotal = 0;
  game.enemiesKilled = 0;
  game.shotsFired = 0;
  game.enemyHits = 0;
  game.cameraY = 0;
  game.backgroundOffset = 0;
  game.animationFrame = 0;
  
  // Reset boosts and states
  game.activeBoost = null;
  game.boostEndTick = 0;
  game.rocketActive = false;
  game.rocketEndTick = 0;
  game.shieldActive = false;
  game.shieldEndTick = 0;
  game.shootingEnabled = true;
  game.bulletsRemaining = 10;
  game.speedModifier = 1.0;
  game.speedModEndTick = 0;
  game.magnetActive = false;
  game.magnetEndTick = 0;
  game.shootCooldown = 0;
  game.shootPulse = false;
  game.events = [];

  // Reset run-end state
  game.gameOver = false;
  game.gameOverReason = null;
  game.deathEndTick = 0;

  // Reset touch controls
  game.isTouching = false;
  game.touchStartX = 0;
  game.touchStartY = 0;
  game.touchCurrentX = 0;
  game.touchTargetX = 0;
  game.touchDragDX = 0;
  game.touchMoved = false;

  // Auto-recalibrate tilt neutral at the start of each run — the first
  // orientation reading becomes "centre", so the player never calibrates by hand.
  game.tiltHasBaseline = false;

  // Reset death state
  game.hitEnemy = false;
  game.deathTime = 0;
  
  // Reset physics
  game.gravity = 0.3;
  game.jumpPower = -9;
  game.moveSpeed = 4.5;
  

// Create initial stars - just a few to start
game.stars = [];
for (let i = 0; i < 30; i++) {
  game.stars.push({
    x: Math.random() * canvasWidth,
    y: Math.random() * canvasHeight,
    size: Math.random() * 2 + 0.5,
    brightness: Math.random() * 0.7 + 0.3
  });
}
  
  // Create initial platforms
  game.platforms.push({
    x: 170,
    y: 500,
    w: 68,
    h: 20,
    type: 'static',
    broken: false,
    hasSpring: false
  });
  
  for (let i = 1; i < 10; i++) {
    game.platforms.push({
      x: game.rng() * (canvasWidth - 68),
      y: 500 - (i * 80),
      w: 68,
      h: 20,
      type: 'static',
      broken: false,
      hasSpring: false
    });
  }
};